import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { useRecaptcha } from "../hooks/useRecaptcha";

function GoogleButton({ label = "Continue with Google", testid = "google-signin" }) {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-semibold hover:bg-muted transition">
      <svg viewBox="0 0 24 24" className="w-4 h-4">
        <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.4-1.6 4.1-5.4 4.1-3.2 0-5.9-2.7-5.9-6s2.6-6 5.9-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.5 14.6 2.5 12 2.5 6.7 2.5 2.5 6.7 2.5 12S6.7 21.5 12 21.5c6.9 0 9.5-4.9 9.5-7.4 0-.5 0-.9-.1-1.3H12z" />
      </svg>
      {label}
    </button>
  );
}

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const params = new URLSearchParams(loc.search);
      nav(params.get("next") || "/dashboard");
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout title="Welcome back">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" value={email} onChange={setEmail} type="email" testid="login-email" />
        <Field label="Password" value={password} onChange={setPassword} type="password" testid="login-password" />
        <div className="flex justify-end -mt-2">
          <Link to="/forgot-password" className="text-xs font-semibold text-primary hover:underline" data-testid="link-forgot-password">
            Forgot password?
          </Link>
        </div>
        <button type="submit" disabled={busy} data-testid="login-submit" className="btn-primary w-full justify-center">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <Divider />
      <GoogleButton />
      <p className="mt-6 text-sm text-muted-foreground">
        No account yet?{" "}
        <Link to="/signup" className="text-primary font-semibold" data-testid="link-signup">Create one</Link>
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        Have an access code?{" "}
        <Link to="/access-code-login" className="text-primary font-semibold" data-testid="link-access-code-login">
          Access Code Login
        </Link>
      </p>
    </AuthLayout>
  );
}

export function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { getToken: getCaptchaToken } = useRecaptcha();

  // Pre-signup verification state — email is REQUIRED, phone is OPTIONAL.
  // `token` becomes truthy once the OTP has been consumed on the backend,
  // and the Create-account button unlocks based on the tokens alone so we
  // never persist a user with an unverified email/phone.
  const [emailSent, setEmailSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailToken, setEmailToken] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailVerifyBusy, setEmailVerifyBusy] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");

  const [phoneSent, setPhoneSent] = useState(false);
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneToken, setPhoneToken] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneVerifyBusy, setPhoneVerifyBusy] = useState(false);
  const [verifiedPhoneDigits, setVerifiedPhoneDigits] = useState("");

  // ── Emergency kill-switch echo ──────────────────────────────────
  // If backend `/auth/pre-signup/phone/send` returns 503 the phone
  // step is disabled account-wide (SMS-pumping mitigation). Track it
  // in local state so we hide the phone UI and let email-only signups
  // continue without stranding the founder on a dead button.
  const [phoneDisabled, setPhoneDisabled] = useState(false);
  // Proactive gate — feature flag from /api/config/features tells us
  // whether phone verification is enabled at all. When it's false the
  // phone field is never rendered, so no user ever hits a broken button.
  const { phone_signup_enabled: phoneSignupEnabled } = useFeatureFlags();
  const showPhoneStep = phoneSignupEnabled && !phoneDisabled;

  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const refCode = new URLSearchParams(loc.search).get("ref");

  // Founders sign up with a US-formatted 10-digit number (e.g. 310-658-3787).
  // We strip formatting and prepend "+1" server-side. Phone is optional at
  // signup — leave the field empty to skip the SMS OTP step entirely.
  const phoneDigits = phone.replace(/\D/g, "");
  const phoneOk = phoneDigits.length === 10;
  const phoneProvided = showPhoneStep && phoneDigits.length > 0;

  // Verification stays bound to the value the user actually verified, so
  // editing the field after Verify invalidates the token and forces a re-verify.
  const emailVerified = !!emailToken && verifiedEmail === email.trim().toLowerCase();
  const phoneVerified = !!phoneToken && verifiedPhoneDigits === phoneDigits;

  const onPhoneChange = (raw) => {
    let digits = raw.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    digits = digits.slice(0, 10);
    let formatted = digits;
    if (digits.length > 6) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    setPhone(formatted);
    // Editing after a successful verify invalidates the token so we don't
    // ship a stale attestation to /auth/register.
    if (phoneToken && digits !== verifiedPhoneDigits) {
      setPhoneToken("");
      setPhoneSent(false);
      setPhoneCode("");
    }
  };

  const onEmailChange = (v) => {
    setEmail(v);
    const next = v.trim().toLowerCase();
    if (emailToken && next !== verifiedEmail) {
      setEmailToken("");
      setEmailSent(false);
      setEmailCode("");
    }
  };

  const sendEmailCode = async () => {
    const target = email.trim().toLowerCase();
    if (!target || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast.error("Enter a valid email first.");
      return;
    }
    setEmailBusy(true);
    try {
      const captcha_token = await getCaptchaToken("pre_signup_email_send");
      await api.post("/auth/pre-signup/email/send", { email: target, captcha_token });
      setEmailSent(true);
      toast.success("Code sent — check your inbox (and spam).");
    } catch (err) { toast.error(formatError(err)); }
    finally { setEmailBusy(false); }
  };

  const verifyEmailCode = async () => {
    const target = email.trim().toLowerCase();
    if (emailCode.length !== 6) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setEmailVerifyBusy(true);
    try {
      const { data } = await api.post("/auth/pre-signup/email/verify", {
        email: target, code: emailCode.trim(),
      });
      setEmailToken(data.email_verification_token);
      setVerifiedEmail(target);
      toast.success("Email verified.");
    } catch (err) { toast.error(formatError(err)); }
    finally { setEmailVerifyBusy(false); }
  };

  const sendPhoneCode = async () => {
    if (!phoneOk) {
      toast.error("Enter a 10-digit US phone number first.");
      return;
    }
    setPhoneBusy(true);
    try {
      const captcha_token = await getCaptchaToken("pre_signup_phone_send");
      await api.post("/auth/pre-signup/phone/send", { phone_e164: `+1${phoneDigits}`, captcha_token });
      setPhoneSent(true);
      toast.success("SMS code sent — check your texts.");
    } catch (err) {
      // 503 = phone endpoint temporarily disabled (SMS-pumping mitigation).
      // Hide the phone flow entirely and let the founder continue email-only.
      if (err?.response?.status === 503) {
        setPhoneDisabled(true);
        setPhone("");
        setPhoneToken("");
        setPhoneSent(false);
        setVerifiedPhoneDigits("");
      }
      toast.error(formatError(err));
    } finally { setPhoneBusy(false); }
  };

  const verifyPhoneCode = async () => {
    if (phoneCode.length !== 6) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setPhoneVerifyBusy(true);
    try {
      const { data } = await api.post("/auth/pre-signup/phone/verify", {
        phone_e164: `+1${phoneDigits}`, code: phoneCode.trim(),
      });
      setPhoneToken(data.phone_verification_token);
      setVerifiedPhoneDigits(phoneDigits);
      toast.success("Phone verified.");
    } catch (err) { toast.error(formatError(err)); }
    finally { setPhoneVerifyBusy(false); }
  };

  // Create button is only enabled when email is verified. If the user typed
  // a phone number, that must also be verified. If they left it empty, phone
  // is skipped entirely and account is created email-only.
  const canSubmit =
    name.trim().length > 0 &&
    password.length >= 6 &&
    emailVerified &&
    (!phoneProvided || phoneVerified);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (!emailVerified) toast.error("Verify your email to continue.");
      else if (phoneProvided && !phoneVerified) toast.error("Verify your phone number or remove it.");
      else toast.error("Fill in every required field.");
      return;
    }
    setBusy(true);
    try {
      await register(
        email.trim().toLowerCase(),
        password,
        name.trim(),
        phoneProvided ? `+1${phoneDigits}` : null,
        { email: emailToken, phone: phoneProvided ? phoneToken : undefined },
      );
      toast.success(refCode ? "Account created — referral applied" : "Account created");
      const params = new URLSearchParams(loc.search);
      nav(params.get("next") || "/account");
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout title="Find Your Path">
      {refCode && (
        <div
          className="mb-5 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-[13px] flex items-start gap-2"
          data-testid="signup-referral-banner"
        >
          <span className="inline-flex items-center rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] shrink-0">
            Invited
          </span>
          <span className="text-foreground">
            You've been invited with referral code <code className="font-mono font-bold text-primary">{refCode}</code>. It'll be applied when you create your account.
          </span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" value={name} onChange={setName} testid="signup-name" />

        {/* ── Email + inline verify ─────────────────────────────────── */}
        <div>
          <Field
            label="Email"
            value={email}
            onChange={onEmailChange}
            type="email"
            testid="signup-email"
          />
          {emailVerified ? (
            <div
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-800"
              data-testid="signup-email-verified"
            >
              <Check className="w-3 h-3" /> Email verified
            </div>
          ) : !emailSent ? (
            <button
              type="button"
              onClick={sendEmailCode}
              disabled={emailBusy || !email.trim()}
              data-testid="signup-email-send-btn"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 transition disabled:opacity-40"
            >
              {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Send verification code
            </button>
          ) : (
            <div className="mt-2 flex items-center gap-2">
              <input
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                placeholder="6-digit code"
                data-testid="signup-email-code"
                className="w-32 rounded-xl bg-white border border-border px-3 py-2 text-sm tracking-[0.3em] focus:outline-none focus:border-primary"
              />
              <button
                type="button"
                onClick={verifyEmailCode}
                disabled={emailVerifyBusy || emailCode.length !== 6}
                data-testid="signup-email-verify-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-40"
              >
                {emailVerifyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Verify
              </button>
              <button
                type="button"
                onClick={sendEmailCode}
                disabled={emailBusy}
                data-testid="signup-email-resend-btn"
                className="text-xs font-semibold text-primary hover:underline disabled:opacity-40"
              >
                Resend
              </button>
            </div>
          )}
        </div>

        {/* ── Phone (optional, hidden when the endpoint is disabled) ── */}
        {showPhoneStep && (
        <div>
          <Field
            label="Phone (optional)"
            value={phone}
            onChange={onPhoneChange}
            type="tel"
            testid="signup-phone"
            placeholder="310-658-3787"
            hint="Optional. If you add one we'll text a 6-digit code to verify it — used for 2FA when you change your email or password."
            required={false}
          />
          {phoneProvided && (
            phoneVerified ? (
              <div
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-800"
                data-testid="signup-phone-verified"
              >
                <Check className="w-3 h-3" /> Phone verified
              </div>
            ) : !phoneSent ? (
              <button
                type="button"
                onClick={sendPhoneCode}
                disabled={phoneBusy || !phoneOk}
                data-testid="signup-phone-send-btn"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 transition disabled:opacity-40"
              >
                {phoneBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Send SMS code
              </button>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <input
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  placeholder="6-digit code"
                  data-testid="signup-phone-code"
                  className="w-32 rounded-xl bg-white border border-border px-3 py-2 text-sm tracking-[0.3em] focus:outline-none focus:border-primary"
                />
                <button
                  type="button"
                  onClick={verifyPhoneCode}
                  disabled={phoneVerifyBusy || phoneCode.length !== 6}
                  data-testid="signup-phone-verify-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-40"
                >
                  {phoneVerifyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Verify
                </button>
                <button
                  type="button"
                  onClick={sendPhoneCode}
                  disabled={phoneBusy}
                  data-testid="signup-phone-resend-btn"
                  className="text-xs font-semibold text-primary hover:underline disabled:opacity-40"
                >
                  Resend
                </button>
              </div>
            )
          )}
        </div>
        )}

        <Field label="Password" value={password} onChange={setPassword} type="password" testid="signup-password" />
        <button
          type="submit"
          disabled={busy || !canSubmit}
          data-testid="signup-submit"
          className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Creating…" : "Create account"}
        </button>
        {!emailVerified && (
          <p className="text-[11px] text-muted-foreground text-center" data-testid="signup-gate-hint">
            Verify your email to create your account.
          </p>
        )}
      </form>
      <Divider />
      <GoogleButton label="Continue with Google" testid="google-signup" />
      <p className="mt-6 text-sm text-muted-foreground">
        Already a member?{" "}
        <Link to="/" className="text-primary font-semibold" data-testid="link-login">Log in</Link>
      </p>
    </AuthLayout>
  );
}

export function AuthLayout({ title, children }) {
  // Auth-specific violet→pink gradient (per brand guidance) — kept inline
  // so we don't override the app-wide `.grad-band` used by the rest of
  // the product (Progress hero, Final Exam result, etc.).
  const AUTH_GRAD = "linear-gradient(103deg, #1C39CE 0%, #6F28BC 58%, #D31787 100%)";
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div
        className="hidden md:flex flex-col justify-between p-12 text-white"
        style={{ backgroundImage: AUTH_GRAD }}
      >
        <Link to="/" className="flex items-center gap-2" data-testid="auth-brand" aria-label="CPG Institute by BrandVault — Home">
          <img
            src="/brand/cpg-logo.png"
            alt="CPG Institute — by BrandVault 360"
            className="block w-auto h-20 md:h-24 lg:h-28 select-none"
            draggable={false}
          />
        </Link>
        <div className="max-w-md">
          <p className="font-display text-4xl leading-tight">
            From Idea To Shelf To Scale.
          </p>
          <p className="mt-6 text-sm text-white/80">The CPG launch workflow, in one workspace.</p>
        </div>
        <p className="text-xs uppercase tracking-widest text-white/70">Warren Ezra · CPG Institute</p>
      </div>
      <div className="flex items-center justify-center p-8 md:p-14">
        <div className="w-full max-w-sm">
          {/* Mobile-only brand — the left promo panel is `hidden md:flex`
              so without this the login screen would open with zero brand
              signal below the tablet breakpoint. Uses the dark-text
              variant since the mobile panel sits on white. */}
          <img
            src="/brand/cpg-logo-dark.png"
            alt="CPG Institute — by BrandVault 360"
            className="md:hidden block h-16 w-auto mb-8 select-none"
            draggable={false}
          />
          <h1 className="font-display text-4xl mb-8">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", testid, hint, placeholder, autoFocus, autoComplete, spellCheck, inputClassName, required = true }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
      <input value={value} type={type} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        className={"mt-1 w-full rounded-xl bg-white border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors " + (inputClassName || "")}
        required={required} />
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Divider() {
  return (
    <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
      <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
    </div>
  );
}
